import { say } from "../../words.ts";
import type { TouchActionId, TouchView } from "./actions.ts";
import type { TouchIconName } from "./icons.ts";

/**
 * What a touch button shows (ADR-061's amendment of 2026-10-31, decision 7), written in place from the page's view so a
 * frame that finds nothing changed allocates nothing (`writeLook`): the word on its face, its accessible name, the icon
 * that varies with the page (Interact's and Stance's; null where the button's own icon stands), and whether it is latched
 * on, dimmed, or under a finger.
 */
export interface ButtonLook {
  label: string;
  name: string;
  icon: TouchIconName | null;
  latched: boolean;
  dimmed: boolean;
  down: boolean;
  ready: boolean;
}

/**
 * The fixed words of the buttons, made once.
 */
const WORDS = { attack: say("touch.attack"), jump: say("touch.jump"), sprint: say("touch.sprint"), tool: say("touch.tool"), sheathe: say("touch.stance.sheathe"), draw: say("touch.stance.draw") } as const;

/**
 * A look to write into, not yet written.
 *
 * @returns The look.
 */
export function newButtonLook(): ButtonLook {
  return { label: "", name: "", icon: null, latched: false, dimmed: false, down: false, ready: false };
}

/**
 * Writes a button's look from the page's view, changing nothing and allocating nothing where it is the same.
 *
 * @param look The button's look.
 * @param id The button.
 * @param view The page's view.
 * @param down Whether a finger is on the button.
 * @returns True when the look changed (always the first time), so the caller redraws the button.
 */
export function writeLook(look: ButtonLook, id: TouchActionId, view: TouchView, down: boolean): boolean {
  let label = "";
  let name = "";
  let icon: TouchIconName | null = null;
  let latched = false;
  let dimmed = false;
  if (id === "attack") {
    label = WORDS.attack;
    name = WORDS.attack;
  } else if (id === "interact") {
    label = view.interact.label;
    name = view.interact.label;
    icon = view.interact.icon;
    dimmed = view.interact.dimmed;
  } else if (id === "jump") {
    name = WORDS.jump;
  } else if (id === "sprint") {
    name = WORDS.sprint;
    latched = view.sprint;
  } else if (id === "stance") {
    name = view.drawn ? WORDS.sheathe : WORDS.draw;
    icon = view.drawn ? "stanceDrawn" : "stanceSheathed";
    latched = view.drawn;
  } else {
    name = WORDS.tool;
    latched = view.toolInHand;
  }
  if (look.ready && look.label === label && look.name === name && look.icon === icon && look.latched === latched && look.dimmed === dimmed && look.down === down) {
    return false;
  }
  look.ready = true;
  look.label = label;
  look.name = name;
  look.icon = icon;
  look.latched = latched;
  look.dimmed = dimmed;
  look.down = down;
  return true;
}
